/**
 * qa-form.tsx — Hỏi đáp nhiều câu hỏi (kiểu HelpKnow): câu hỏi chuẩn + câu hỏi tương tự (mỗi dòng một
 * câu, cùng một câu trả lời), danh mục, câu trả lời; nhập / xuất CSV; sửa nhanh một mục kiến thức theo id
 * (nút "Chỉnh sửa" trong hộp Chi tiết của Kiểm tra hỏi đáp).
 */
import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { Download, FileUp, Loader2, MessageCircleQuestion, Save } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch, Textarea } from '@/components/ui/misc'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { apiError } from '@/lib/api-client'
import {
  useCreateKnowledgeEntry, useUpdateKnowledgeEntry, fetchKnowledgeEntry, type KnowledgeEntry,
} from '@/hooks/use-knowledge'
import { KbCategorySelect } from '../knowledge/kb-category-select'
import { importQaCsv, exportQaCsv, setKnowledgeEnabled } from '@/hooks/use-ai-train-test'
import { parseQuestionLines, QA_CSV_TEMPLATE, downloadText } from './train-lib'

/** Các ô của một hỏi đáp (dùng cho cả thêm mới và sửa). Danh mục = danh mục BÊN TRONG kho của mục. */
export function QaFields({ question, setQuestion, similar, setSimilar, answer, setAnswer, categoryId, setCategoryId, knowledgeBaseId, answerLoading }: {
  question: string; setQuestion: (v: string) => void
  similar: string; setSimilar: (v: string) => void
  answer: string; setAnswer: (v: string) => void
  categoryId: string; setCategoryId: (v: string) => void
  knowledgeBaseId: string | null | undefined
  /** Đang tải đủ câu trả lời (mục bị cắt ngắn trong danh sách). */
  answerLoading?: boolean
}) {
  const { t } = useTranslation('aiTrainUi')
  const count = parseQuestionLines(similar, question).length
  return (
    <div className="space-y-3">
      <div>
        <Label>{t('qa.question')}</Label>
        <Input value={question} onChange={(e) => setQuestion(e.target.value)} placeholder={t('qa.questionPlaceholder')} />
      </div>
      <div>
        <Label>{t('qa.similar')} <span className="font-normal text-muted-foreground">{t('qa.similarHint')}</span></Label>
        <Textarea
          className="min-h-24 text-sm" value={similar} onChange={(e) => setSimilar(e.target.value)}
          placeholder={t('qa.similarPlaceholder')}
        />
        <p className="mt-0.5 text-[11px] text-muted-foreground">{t('qa.similarCount', { count })}</p>
      </div>
      <div>
        <Label>{t('qa.answer')}</Label>
        {answerLoading ? (
          <p className="flex items-center gap-2 py-6 text-xs text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> {t('qa.loadingAnswer')}</p>
        ) : (
          <Textarea
            className="min-h-32 text-sm" value={answer} onChange={(e) => setAnswer(e.target.value)}
            placeholder={t('qa.answerPlaceholder')}
          />
        )}
      </div>
      <div>
        <Label>{t('qa.category')}</Label>
        <KbCategorySelect knowledgeBaseId={knowledgeBaseId} kind="qa" value={categoryId} onChange={setCategoryId} />
      </div>
    </div>
  )
}

/** Thẻ "Hỏi đáp" trong hộp Thêm kiến thức: thêm một hỏi đáp + nhập / xuất CSV — trong MỘT kho. */
export function QaCreatePanel({ knowledgeBaseId, defaultCategoryId = '', onCreated }: {
  /** Kho nhận ('' = máy chủ dùng "Kho mặc định"). */
  knowledgeBaseId?: string
  defaultCategoryId?: string
  onCreated?: (e: KnowledgeEntry) => void
}) {
  const { t } = useTranslation('aiTrainUi')
  const create = useCreateKnowledgeEntry()
  const qc = useQueryClient()
  const [question, setQuestion] = useState('')
  const [similar, setSimilar] = useState('')
  const [answer, setAnswer] = useState('')
  const [categoryId, setCategoryId] = useState(defaultCategoryId)
  const [importing, setImporting] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  async function submit() {
    if (!question.trim() || !answer.trim()) { toast.error(t('qa.needBoth')); return }
    try {
      const e = await create.mutateAsync({
        type: 'faq', format: 'qa', risk: 'low', source: 'staff_manual',
        title: question.trim(), content: answer.trim(), categoryId: categoryId || null,
        knowledgeBaseId: knowledgeBaseId || null,
        questions: parseQuestionLines(similar, question),
      })
      toast.success(t('qa.added'))
      setQuestion(''); setSimilar(''); setAnswer('')
      onCreated?.(e)
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  async function onFile(file: File) {
    setImporting(true)
    try {
      const r = await importQaCsv(await file.text(), categoryId, knowledgeBaseId)
      const extra = r.errors.length ? t('qa.importErrors', { count: r.errors.length, lines: r.errors.slice(0, 5).map((e) => e.line).join(', ') }) : ''
      const cats = r.unknownCategories.length ? t('qa.unknownCategories', { list: r.unknownCategories.join(', ') }) : ''
      toast.success(t('qa.importDone', { created: r.created, updated: r.updated, extra, cats }))
      qc.invalidateQueries({ queryKey: ['knowledge'] })
    } catch (err) {
      toast.error(apiError(err))
    } finally {
      setImporting(false)
    }
  }

  async function exportAll() {
    try {
      downloadText(await exportQaCsv(categoryId, knowledgeBaseId), 'hoi-dap.csv')
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  return (
    <div className="space-y-3">
      <QaFields {...{ question, setQuestion, similar, setSimilar, answer, setAnswer, categoryId, setCategoryId, knowledgeBaseId }} />
      <div className="flex flex-wrap items-center justify-between gap-2 border-t pt-3">
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={importing}>
            {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />} {t('qa.importCsv')}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => downloadText(QA_CSV_TEMPLATE, 'mau-hoi-dap.csv')}>
            <Download className="h-4 w-4" /> {t('qa.downloadTemplate')}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => void exportAll()}>
            <Download className="h-4 w-4" /> {t('qa.exportQa')}
          </Button>
          <input
            ref={fileRef} type="file" accept=".csv,text/csv" hidden
            onChange={(e) => { const f = e.target.files?.[0]; if (f) void onFile(f); e.target.value = '' }}
          />
        </div>
        <Button onClick={() => void submit()} disabled={create.isPending}>
          {create.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageCircleQuestion className="h-4 w-4" />} {t('qa.addQa')}
        </Button>
      </div>
      <p className="text-[11px] text-muted-foreground">
        {t('qa.csvNote')}
      </p>
    </div>
  )
}

/**
 * Sửa nhanh một mục kiến thức theo id (từ "Nguồn câu trả lời"): hỏi đáp có câu hỏi tương tự; tài liệu
 * chỉnh tiêu đề + nội dung. Có công tắc bật / tắt cho AI.
 */
export function EditKnowledgeByIdDialog({ id, onClose, onSaved }: { id: string; onClose: () => void; onSaved?: () => void }) {
  const { t } = useTranslation('aiTrainUi')
  const update = useUpdateKnowledgeEntry()
  const qc = useQueryClient()
  const [entry, setEntry] = useState<KnowledgeEntry | null>(null)
  const [question, setQuestion] = useState('')
  const [similar, setSimilar] = useState('')
  const [answer, setAnswer] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [enabled, setEnabled] = useState(true)

  useEffect(() => {
    let alive = true
    fetchKnowledgeEntry(id)
      .then((e) => {
        if (!alive) return
        setEntry(e)
        setQuestion(e.title ?? '')
        setSimilar((Array.isArray(e.questions) ? e.questions : []).join('\n'))
        setAnswer(e.content)
        setCategoryId(e.categoryId ?? '')
        setEnabled(e.status !== 'archived')
      })
      .catch((err) => { toast.error(apiError(err)); onClose() })
    return () => { alive = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- chỉ tải lại khi đổi mục (onClose thường là hàm nội tuyến)
  }, [id])

  const isQa = entry?.format === 'qa'

  async function save() {
    if (!entry) return
    if (isQa && (!question.trim() || !answer.trim())) { toast.error(t('qa.needBoth')); return }
    try {
      await update.mutateAsync({
        id: entry.id,
        data: {
          title: question.trim() || null, content: answer, categoryId: categoryId || null,
          ...(isQa ? { questions: parseQuestionLines(similar, question) } : {}),
          changeNote: 'Sửa từ Kiểm tra hỏi đáp', // i18n-ignore: ghi chú lưu vào lịch sử phiên bản kiến thức
        },
      })
      if (enabled !== (entry.status !== 'archived')) await setKnowledgeEnabled(entry.id, enabled)
      qc.invalidateQueries({ queryKey: ['knowledge'] })
      toast.success(t('qa.savedRelearn'))
      onSaved?.()
      onClose()
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{isQa ? t('qa.editQa') : t('qa.editKnowledge')}</DialogTitle>
          <DialogDescription>{t('qa.editDesc')}</DialogDescription>
        </DialogHeader>
        {!entry ? (
          <p className="flex items-center gap-2 py-8 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> {t('qa.loading')}</p>
        ) : isQa ? (
          <QaFields {...{ question, setQuestion, similar, setSimilar, answer, setAnswer, categoryId, setCategoryId, knowledgeBaseId: entry.knowledgeBaseId }} />
        ) : (
          <div className="space-y-3">
            <div>
              <Label>{t('common:fields.title')}</Label>
              <Input value={question} onChange={(e) => setQuestion(e.target.value)} />
            </div>
            <div>
              <Label>{t('common:fields.content')}</Label>
              <Textarea className="min-h-56 font-mono text-xs" value={answer} onChange={(e) => setAnswer(e.target.value)} />
            </div>
            <div>
              <Label>{t('qa.category')}</Label>
              <KbCategorySelect knowledgeBaseId={entry.knowledgeBaseId} kind="qa" value={categoryId} onChange={setCategoryId} />
            </div>
          </div>
        )}
        {entry && (
          <div className="flex items-center justify-between gap-2 border-t pt-3">
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={enabled} onCheckedChange={setEnabled} /> {t('qa.allowAi')}
            </label>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={onClose}>{t('common:actions.close')}</Button>
              <Button onClick={() => void save()} disabled={update.isPending}>
                {update.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {t('common:actions.save')}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
